import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { APIService } from "src/app/api/api.service";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-nodegroup-dashboard",
  templateUrl: "./nodegroup-dashboard.component.html",
  styleUrls: ["../eks-requests.styles.css"],
})
export class NodegroupDashboardComponent implements OnInit, OnDestroy {
  private role: string = (localStorage.getItem("role") || "").toLowerCase();
  urlPrefix: string = this.role == "admin" ? "admin" : "client";
  apiBaseUrl: string = environment.apiURL || APIService.API_ENDPOINTV3;

  loading = false;
  error = "";
  rows: any[] = [];
  accountId = "";
  region = "";
  clusterName = "";
  clusterRequestId = "";
  returnToServiceCatalog = false;
  currentMessage: Subscription;

  nextKey = "";
  hasMore = false;
  readonly pageSize = 20;
  private refreshSequence = 0;

  showNodeGroupModal = false;
  nodeGroupContext: any = null;

  constructor(
    private apiServer: APIService,
    private notifier: NotifierService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.clusterName = this.normalize(this.route.snapshot.paramMap.get("cluster_name"));
    this.accountId = this.normalize(this.route.snapshot.queryParamMap.get("aws_account_id")) || this.normalize(localStorage.getItem("accountId"));
    this.region = this.normalize(this.route.snapshot.queryParamMap.get("region")) || this.normalize(localStorage.getItem("regionId"));
    this.clusterRequestId = this.normalize(this.route.snapshot.queryParamMap.get("cluster_request_id"));
    this.returnToServiceCatalog = this.normalize(this.route.snapshot.queryParamMap.get("source")) === "service-catalog";

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) return;
      try {
        const d = JSON.parse(msg);
        if (d.value == null || d.source != "notifier") return;
        if (d.key == "accountId") {
          const normalizedVal = this.normalize(d.value);
          if (this.accountId !== normalizedVal) {
            this.accountId = normalizedVal;
            this.refresh();
          }
        }
        if (d.key == "regionId") {
          const normalizedVal = this.normalize(d.value);
          if (this.region !== normalizedVal) {
            this.region = normalizedVal;
            this.refresh();
          }
        }
      } catch {}
    });

    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.currentMessage) this.currentMessage.unsubscribe();
  }

  private normalize(v: any): string {
    const s = (v ?? "").toString().trim();
    return !s || s === "null" || s === "undefined" ? "" : s;
  }

  private headers() {
    return { Authorization: localStorage.getItem("t") };
  }

  private buildApiUrl(path: string) {
    return `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
  }

  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      return typeof r == "string" ? null : r;
    } catch {
      return null;
    }
  }

  refresh() {
    const sequence = ++this.refreshSequence;
    this.refreshForContext(sequence);
  }

  private async refreshForContext(sequence: number) {
    const matchingClusterRequest = await this.findMatchingEksClusterRequest();
    if (sequence !== this.refreshSequence) return;
    if (!matchingClusterRequest) {
      this.clearDashboardState();
      this.redirectToEksDashboard();
      return;
    }
    this.clusterRequestId = this.normalize(matchingClusterRequest?.request_id) || this.clusterRequestId;
    this.loadRequests(true);
  }

  private clearDashboardState() {
    this.rows = [];
    this.nextKey = "";
    this.hasMore = false;
    this.error = "";
    this.loading = false;
    this.closeNodeGroupModal();
    this.notifier.loading(false);
  }

  private async findMatchingEksClusterRequest(): Promise<any | null> {
    if (!this.accountId || !this.region || !this.clusterName) return null;

    const path = `apis/eks_launch/api/eks/requests?aws_account_id=${encodeURIComponent(this.accountId)}&region=${encodeURIComponent(this.region)}&limit=100`;
    const r = await this.get(this.buildApiUrl(path));
    const items = Array.isArray(r?.items) ? r.items : [];
    if (!r || items.length === 0) return null;

    const clusterName = this.clusterName.toLowerCase();
    const clusterRequestId = this.clusterRequestId.toLowerCase();
    return items.find((row: any) => {
      const rowClusterName = this.normalize(row?.cluster_name).toLowerCase();
      const rowRequestId = this.normalize(row?.request_id).toLowerCase();
      return rowClusterName === clusterName || (!!clusterRequestId && rowRequestId === clusterRequestId);
    }) || null;
  }

  async loadRequests(reset: boolean = false) {
    if (reset) {
      this.rows = [];
      this.nextKey = "";
      this.hasMore = false;
    }
    this.error = "";
    if (!this.accountId || !this.region || !this.clusterName) {
      this.error = "Account, region, and cluster are required.";
      return;
    }

    this.loading = true;
    this.notifier.loading(true);
    let path = `apis/eks_launch/api/eks/nodegroup/requests?aws_account_id=${encodeURIComponent(this.accountId)}&region=${encodeURIComponent(this.region)}&cluster_name=${encodeURIComponent(this.clusterName)}&limit=${this.pageSize}`;
    if (!reset && this.nextKey) path += `&next_key=${encodeURIComponent(this.nextKey)}`;

    const r = await this.get(this.buildApiUrl(path));
    this.loading = false;
    this.notifier.loading(false);
    if (!r) {
      this.error = "Unable to load node group requests.";
      return;
    }
    const items = Array.isArray(r.items) ? r.items : [];
    this.rows = reset ? items : [...this.rows, ...items];
    this.nextKey = r.next_key || "";
    this.hasMore = !!this.nextKey;
  }

  openNodeGroupModal() {
    this.nodeGroupContext = {
      request_id: this.clusterRequestId,
      cluster_name: this.clusterName,
      region: this.region,
    };
    this.showNodeGroupModal = true;
  }

  closeNodeGroupModal() {
    this.showNodeGroupModal = false;
    this.nodeGroupContext = null;
  }

  onNodeGroupCreated() {
    this.closeNodeGroupModal();
    this.refresh();
  }

  detailQueryParams(row: any) {
    const params: any = {
      aws_account_id: this.accountId,
      region: row?.region || this.region,
      cluster_name: row?.cluster_name || this.clusterName,
    };
    if (this.returnToServiceCatalog) {
      params.source = "service-catalog";
    }
    return params;
  }

  back() {
    this.redirectToEksDashboard();
  }

  private redirectToEksDashboard() {
    if (this.returnToServiceCatalog) {
      this.router.navigate(["/dash/operations/service-catalog"], { queryParams: { service: "eks" } });
      return;
    }
    this.router.navigate(["/dash/operations/eks-dashboard"]);
  }
}
